// Color math behind firm branding, shared by the client form (which renders the
// real welcome screen) and the console's live preview (which claims to show it).
//
// It lives here rather than in either app because the two have to agree to the
// pixel: a preview computing its own contrast is a preview that lies to the
// lawyer. Pure functions, no Convex, no React — this file is not part of the
// deployment, only of the package.

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsl {
  h: number;
  s: number; // 0-100
  l: number; // 0-100
}

export function parseHex(hex: string): Rgb | null {
  if (typeof hex !== "string") return null;
  const clean = hex.trim().replace(/^#/, "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export function hexToHsl(hex: string): Hsl | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sat = s / 100;
  const lig = l / 100;
  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = lig - c / 2;

  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  return {
    r: Math.round((rgb[0] + m) * 255),
    g: Math.round((rgb[1] + m) * 255),
    b: Math.round((rgb[2] + m) * 255),
  };
}

const round = (n: number) => Math.round(n * 10) / 10;

/** The `H S% L%` triplet Tailwind expects — every token is `hsl(var(--token))`. */
export function hslTriplet({ h, s, l }: Hsl): string {
  return `${round(h)} ${round(s)}% ${round(l)}%`;
}

/** A ready-to-use CSS color, for inline styles that don't go through a token. */
export function hslCss(hsl: Hsl): string {
  return `hsl(${hslTriplet(hsl)})`;
}

/** Hex → Tailwind triplet. Null on anything unparseable, so callers can bail. */
export function hexToHslTriplet(hex: string): string | null {
  const hsl = hexToHsl(hex);
  return hsl ? hslTriplet(hsl) : null;
}

/** WCAG relative luminance (gamma-corrected), 0 (black) → 1 (white). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function rgbContrast(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** WCAG contrast ratio of an HSL color against a hex background, 1 → 21. */
export function contrastRatio(color: Hsl, backgroundHex: string): number {
  const bg = parseHex(backgroundHex);
  return bg ? rgbContrast(hslToRgb(color), bg) : 1;
}

// WCAG AA: 4.5:1 for body text, 3:1 for icons and other non-text graphics.
export const TEXT_CONTRAST = 4.5;
export const GRAPHIC_CONTRAST = 3;

/**
 * Nudges a color's lightness — hue and saturation untouched, so the brand still
 * reads as itself — until it clears `minRatio` against `onHex`. Falls back to
 * plain white or black when even that isn't enough (a mid-gray background can't
 * host its own hue at 4.5:1).
 */
export function ensureReadable(color: Hsl, onHex: string, minRatio: number): Hsl {
  const bg = parseHex(onHex);
  if (!bg) return color;
  if (rgbContrast(hslToRgb(color), bg) >= minRatio) return color;

  // Light background → go darker, dark background → go lighter.
  const step = relativeLuminance(bg) > 0.18 ? -2 : 2;
  for (let l = color.l + step; l >= 0 && l <= 100; l += step) {
    const candidate = { ...color, l };
    if (rgbContrast(hslToRgb(candidate), bg) >= minRatio) return candidate;
  }
  return { ...color, l: step > 0 ? 100 : 0 };
}

/** Perceived luminance, 0 (black) → 1 (white). */
function perceivedLuminance({ r, g, b }: Rgb): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Whether text on this color has to flip to a light ink. */
export function isDarkHex(hex: string): boolean {
  const rgb = parseHex(hex);
  return rgb ? perceivedLuminance(rgb) < 0.5 : false;
}

// The form's index.css tokens, as HSL so they can be nudged for contrast.
export const WHITE: Hsl = { h: 0, s: 0, l: 100 };
export const INK: Hsl = { h: 214, s: 32, l: 15 }; // --foreground
export const MUTED_FOREGROUND: Hsl = { h: 214, s: 15, l: 45 };
/** --muted-foreground once the surface under it went dark. */
export const MUTED_FOREGROUND_ON_DARK: Hsl = { h: 210, s: 20, l: 80 };
/** --muted and --border as shipped, for surfaces light enough to keep them. */
export const MUTED_SURFACE: Hsl = { h: 210, s: 15, l: 94 };
export const OUTLINE: Hsl = { h: 214, s: 20, l: 90 };

/**
 * Ink for a label sitting *on* a solid brand color (button text, badges).
 * Perceived luminance rather than WCAG on purpose: it's the threshold that
 * keeps white on Formio blue, which is the shipped look.
 */
export function foregroundOnHsl(color: Hsl): Hsl {
  return perceivedLuminance(hslToRgb(color)) > 0.62 ? INK : WHITE;
}

/** Same, from a hex string. */
export function foregroundOn(hex: string): Hsl {
  const rgb = parseHex(hex);
  if (!rgb) return WHITE;
  return perceivedLuminance(rgb) > 0.62 ? INK : WHITE;
}

/**
 * Surfaces and outlines drawn on top of a dark card: derived from the card
 * itself so they keep its hue instead of staying near-white.
 */
export function surfacesOnCard(card: Hsl): { muted: Hsl; outline: Hsl } {
  return {
    muted: { ...card, l: Math.min(card.l + 10, 100) },
    outline: { h: card.h, s: Math.min(card.s, 30), l: Math.min(card.l + 22, 100) },
  };
}

/** The four ink/surface tokens a given surface color needs under it. */
export interface SurfaceTokens {
  foreground: Hsl;
  mutedForeground: Hsl;
  muted: Hsl;
  outline: Hsl;
}

/**
 * Everything drawn *on* one surface, whether that surface is the page background
 * or a card. A dark surface flips to light ink and derives its slabs and outlines
 * from itself, so they keep its hue instead of staying near-white; a light one
 * keeps the shipped tokens, with the ink raised to AA in case the firm picked a
 * mid-tone the stock ink can't clear.
 */
export function surfaceTokens(surfaceHex: string): SurfaceTokens {
  const hsl = hexToHsl(surfaceHex);
  if (!hsl) {
    return { foreground: INK, mutedForeground: MUTED_FOREGROUND, muted: MUTED_SURFACE, outline: OUTLINE };
  }
  if (isDarkHex(surfaceHex)) {
    const { muted, outline } = surfacesOnCard(hsl);
    return {
      foreground: WHITE,
      mutedForeground: MUTED_FOREGROUND_ON_DARK,
      muted,
      outline,
    };
  }
  return {
    foreground: ensureReadable(INK, surfaceHex, TEXT_CONTRAST),
    mutedForeground: ensureReadable(MUTED_FOREGROUND, surfaceHex, TEXT_CONTRAST),
    muted: MUTED_SURFACE,
    outline: OUTLINE,
  };
}
